import axios from 'axios';
import { getWindow } from './getWindow';
import type { BusinessUI } from '../ui/business/types';
import type { PostUI } from '../ui/posts/types';
const envPeriod = process.env.REACT_APP_ANALYTICS_REQUESTS_PERIOD;
const errorExtendTime = process.env.REACT_APP_ANALYTICS_ERROR_EXTEND_TIME;
const envBulkSize = process.env.REACT_APP_ANALYTICS_BULK_SIZE;
const envMaxEventHistorySize = process.env.REACT_APP_ANALYTICS_MAX_EVENT_HISTORY_SIZE;
const DEFAULT_PERIOD = envPeriod ? parseInt(envPeriod, 10) : 2000;
const DEFAULT_BULK_SIZE = envBulkSize ? parseInt(envBulkSize, 10) : 100;
const MAX_EVENT_HISTORY_SIZE = envMaxEventHistorySize ? parseInt(envMaxEventHistorySize, 10) : 1000;
const ERROR_EXTEND_TIME = errorExtendTime ? parseInt(errorExtendTime, 10) : 2000;

const getMordaPlatformFromUrl = (): Record<string, string> => {
    const window = getWindow();

    const params = new URLSearchParams(window?.location.search);
    const morda_platform = params.get('morda_platform');

    if (morda_platform) {
        return { morda_platform };
    }

    return {};
};

export enum EventSource {
    BUSINESS = 'business',
    WIDGET = 'widget',
    XIAOMI_WIDGET = 'xiaomi_widget',
    PROFILE = 'profile',
    SEARCH = 'search',
    POST = 'post',
    SEARCH_APP_FULLSCREEN = 'search_app_fullscreen',
    COLLECTION = 'collection',
    PRODUCTS = 'catalog',
    MULTICOLUMN = 'multicolumn',
    PARTNER_REGISTRATION = 'partner_registration',
}

export enum PostVisibleEventTypes {
    POST_VISIBLE = 5,
    POST_HIDDEN = 6,
    GRID_POST_VISIBLE = 31,
    GRID_POST_HIDDEN = 32,
    GRID_POST_HOVER = 5,
    GRID_POST_HOVER_CANCEL = 6,
    // Показ поста спустя 2 секунды после пападания хотя бы 1 пикселя во вьюпорт
    GRID_POST_VISIBLE_2_SEC = 41,
}

/** Расшифровка событий https://a.yandex-team.ru/arcadia/inspire/backend/proto/recommender/events.proto?rev=r18668015#L27*/
export enum EventTypes {
    COLLECTION_OPENED = 29,
    REVIEW_CLICK = 1010,
    PRODUCTS_UNROLL = 27,
}

const getPostVisibleEventType = (isInViewport: boolean, isGrid?: boolean) => {
    if (isGrid) {
        return isInViewport
            ? PostVisibleEventTypes.GRID_POST_VISIBLE
            : PostVisibleEventTypes.GRID_POST_HIDDEN;
    }

    return isInViewport ? PostVisibleEventTypes.POST_VISIBLE : PostVisibleEventTypes.POST_HIDDEN;
};

export interface AnalyticEvent {
    object_id?: string;
    content_id?: string;
    clickout_target?: string;
    event_type: number;
    time?: string;
    event_source: EventSource;
    clickout_url?: string;
    post_number?: number;
    slider_content_number?: number;
    not_by_user?: boolean;
    dj_request_id?: string;
    log_context?: string;
    product_id?: string;
    post_id?: string;
    business_id?: string;
    products?: string[];
    collection_id?: string;
    is_boosted?: boolean;
    columns_count?: number;
}

export enum BusinessTransitionType {
    IN = 1004,
    OUT = 1005,
}

export enum VideoStates {
    VIDEO_STARTED = 12,
    VIDEO_FINISHED = 13,
    VIDEO_MUTED = 16,
    VIDEO_UN_MUTED = 17,
    VIDEO_PAUSED = 18,
    VIDEO_RESUMED = 19,
}

export interface VideoStateEventParams {
    eventType: VideoStates;
    wasNotTriggeredByUser?: boolean;
}

interface IAnalyticsServiceParams {
    postId?: PostUI['id'];
    objectId?: PostUI['id'];
    eventSource: EventSource;
    productId?: string;
    businessId?: string;
    mediaId?: string;
    djRequestId?: string;
    logContext?: string;
    clickoutUrl?: string;
    target?: string;
    collectionId?: string;
    isBoosted?: boolean;
    columns?: number;
    sliderContentNumber?: number;
    wasNotTriggeredByUser?: boolean;
    postNumber?: number;
    products?: string[];
}

export class AnalyticsService {
    private events: AnalyticEvent[] = [];
    private bulkSize: number = DEFAULT_BULK_SIZE;
    private intervalPeriod = DEFAULT_PERIOD;
    private requestParams: Record<string, string> = {};

    constructor(params?: { bulkSize?: number; requestParams?: Record<string, string> }) {
        this.bulkSize = params?.bulkSize || DEFAULT_BULK_SIZE;
        this.requestParams = params?.requestParams || {};
        this.sendBulk();
    }

    protected formatEventParams(params: IAnalyticsServiceParams, eventType: number): AnalyticEvent {
        const event: AnalyticEvent = {
            object_id: params.objectId,
            post_id: params.postId,
            content_id: params.mediaId,
            event_type: eventType,
            clickout_target: params.target,
            clickout_url: params.clickoutUrl,
            event_source: params.eventSource,
            dj_request_id: params.djRequestId,
            log_context: params.logContext,
            product_id: params.productId,
            business_id: params.businessId,
            collection_id: params.collectionId,
            is_boosted: params.isBoosted,
            columns_count: params.columns,
            slider_content_number: params.sliderContentNumber,
            not_by_user: params.wasNotTriggeredByUser,
            post_number: params.postNumber,
            products: params.products,
        };

        return event;
    }

    private pushEvent = (params: IAnalyticsServiceParams, eventType: number) => {
        const event = this.formatEventParams(params, eventType);

        this.events.push({ ...event, time: new Date().toISOString() });

        if (this.events.length > MAX_EVENT_HISTORY_SIZE) {
            this.events.shift();
        }
    };

    public trackClickOut(params: {
        postId?: PostUI['id'];
        eventSource: EventSource;
        productId?: string;
        businessId?: string;
        mediaId?: string;
        djRequestId?: string;
        logContext?: string;
        clickoutUrl?: string;
        target?: string;
        collectionId?: string;
        isBoosted?: boolean;
        columns?: number;
    }) {
        this.pushEvent({ ...params, objectId: params.postId }, 9);
    }

    public trackShare(params: {
        postId: PostUI['id'];
        mediaId: string;
        eventSource: EventSource;
        djRequestId?: string;
        collectionId?: string;
        logContext?: string;
        columns?: number;
    }) {
        this.pushEvent({ ...params, objectId: params.postId }, 8);
    }

    public trackCollectionShare(params: {
        eventSource: EventSource;
        collectionId: string;
        columns?: number;
    }) {
        this.pushEvent(params, 8);
    }

    public trackSlideMoved(params: {
        postId: PostUI['id'];
        mediaId: string;
        eventSource: EventSource;
        sliderContentNumber: number;
        djRequestId?: string;
        logContext?: string;
        collectionId?: string;
        columns?: number;
    }) {
        this.pushEvent({ ...params, objectId: params.postId }, 7);
    }

    public trackPostExpanded(params: {
        postId: PostUI['id'];
        mediaId: string;
        eventSource: EventSource;
        sliderContentNumber: number;
        djRequestId?: string;
        logContext?: string;
        collectionId?: string;
        columns?: number;
    }) {
        this.pushEvent({ ...params, objectId: params.postId }, 20);
    }

    public trackVideoStateChange(params: {
        postId: PostUI['id'];
        mediaId: string;
        eventSource: EventSource;
        state: VideoStateEventParams;
        djRequestId?: string;
        logContext?: string;
        collectionId?: string;
        columns?: number;
    }) {
        this.pushEvent(
            {
                ...params,
                objectId: params.postId,
                wasNotTriggeredByUser: params.state.wasNotTriggeredByUser,
            },
            params.state.eventType,
        );
    }

    public trackPostVisible(params: {
        postId: PostUI['id'];
        mediaId: string;
        eventSource: EventSource;
        isInViewport: boolean;
        isGrid?: boolean;
        postNumber: number;
        djRequestId?: string;
        logContext?: string;
        collectionId?: string;
        products?: string[];
        isBoosted?: boolean;
        columns?: number;
    }) {
        this.pushEvent(
            { ...params, objectId: params.postId },
            getPostVisibleEventType(params.isInViewport, params.isGrid),
        );
    }

    public trackPostVisible2Sec(params: {
        postId: PostUI['id'];
        mediaId: string;
        eventSource: EventSource;
        postNumber: number;
        djRequestId?: string;
        logContext?: string;
        collectionId?: string;
        products?: string[];
        isBoosted?: boolean;
        columns?: number;
    }) {
        this.pushEvent(
            { ...params, objectId: params.postId },
            PostVisibleEventTypes.GRID_POST_VISIBLE_2_SEC,
        );
    }

    public trackBusinessTransition(params: {
        businessId: BusinessUI['id'];
        eventSource: EventSource;
        businessTransitionType: BusinessTransitionType;
        columns?: number;
    }) {
        this.pushEvent(params, params.businessTransitionType);
    }

    public trackProductsUnroll(params: {
        products: string[];
        businessId?: BusinessUI['id'];
        postId: PostUI['id'];
        postNumber: number;
        djRequestId?: string;
        logContext?: string;
        eventSource: EventSource;
        collectionId?: string;
        columns?: number;
    }) {
        this.pushEvent({ ...params, objectId: params.postId }, EventTypes.PRODUCTS_UNROLL);
    }

    public trackGridTap(params: {
        postId: PostUI['id'];
        postNumber: number;
        eventSource: EventSource;
        businessId?: BusinessUI['id'];
        collectionId?: string;
        logContext?: string;
        djRequestId?: string;
        columns?: number;
    }) {
        this.pushEvent(params, 33);
    }

    public trackOpenCatalog(params: {
        postId?: PostUI['id'];
        eventSource: EventSource;
        businessId?: BusinessUI['id'];
        djRequestId?: string;
        logContext?: string;
        columns?: number;
    }) {
        this.pushEvent(params, 37);
    }

    public gridPostHover(params: {
        postId: PostUI['id'];
        eventSource: EventSource;
        businessId?: BusinessUI['id'];
        djRequestId?: string;
        logContext?: string;
        columns?: number;
    }) {
        this.pushEvent(
            { ...params, objectId: params.postId },
            PostVisibleEventTypes.GRID_POST_HOVER,
        );
    }

    public gridPostHoverCancel(params: {
        postId: PostUI['id'];
        eventSource: EventSource;
        businessId?: BusinessUI['id'];
        djRequestId?: string;
        logContext?: string;
        columns?: number;
    }) {
        this.pushEvent(
            { ...params, objectId: params.postId },
            PostVisibleEventTypes.GRID_POST_HOVER_CANCEL,
        );
    }

    public trackCollectionOpened(params: {
        collectionId: string;
        djRequestId?: string;
        logContext?: string;
        columns?: number;
    }) {
        this.pushEvent(
            {
                ...params,
                postId: params.collectionId,
                objectId: params.collectionId,
                eventSource: EventSource.COLLECTION,
            },
            EventTypes.COLLECTION_OPENED,
        );
    }

    public trackReviewClick(params: {
        eventSource: EventSource;
        businessId?: BusinessUI['id'];
        clickoutUrl?: string;
    }) {
        this.pushEvent(params, EventTypes.REVIEW_CLICK);
    }

    private sendBulk = async () => {
        let lastBulk: AnalyticEvent[] = [];

        try {
            lastBulk = this.events.splice(Math.max(this.events.length - 1 - this.bulkSize, 0));
            if (lastBulk.length) {
                await axios.post(
                    '/user/actions',
                    {
                        events: lastBulk,
                    },
                    {
                        params: this.requestParams,
                    },
                );
                this.intervalPeriod = DEFAULT_PERIOD;
            }
            setTimeout(() => this.sendBulk(), this.intervalPeriod);
        } catch (err) {
            if (axios.isAxiosError(err) && err.response?.status !== 400) {
                if (lastBulk.length) {
                    this.events.push(...lastBulk);
                }

                this.intervalPeriod += ERROR_EXTEND_TIME;
            }
            setTimeout(() => this.sendBulk(), this.intervalPeriod);
        }
    };
}

export const analyticsService = new AnalyticsService({
    bulkSize: DEFAULT_BULK_SIZE,
    requestParams: { ...getMordaPlatformFromUrl() },
});

export const trackViewPostBoost = (trackUrl: string) => {
    axios.get(trackUrl);
};
